<template>
    <div class="relative z-20" v-click-outside="() => setDropdownOpen(false)">
        <button
            v-if="hoursData"
            class="w-full rounded flex items-center gap-2 focus:outline-none hover:text-slate-500 active:text-slate-500"
            :class="buttonColor"
            @click.prevent="() => setDropdownOpen(!dropdownOpen)"
        >
            <LoungeIcon icon="clock" :pathClass="buttonColor" />
            <span
                class="flex-1 h-5 justify-start items-center gap-1 flex text-sm font-normal leading-tight whitespace-nowrap overflow-hidden text-ellipsis"
            >
                {{ todaysHours.time }} {{ openNowText }}
            </span>
            <span
                class="flex-shrink-0 flex justify-center w-3 h-3 p-0.5 items-center"
            >
                <Icon icon="chevron-down" height="12" width="12" />
            </span>
        </button>
        <span
            v-if="!hoursData && timing"
            class="h-5 justify-start items-center gap-2 flex text-slate-500 text-sm font-normal leading-tight"
        >
            <LoungeIcon icon="clock" :pathClass="buttonColor" />

            {{ timing }}
        </span>
        <div
            v-if="dropdownOpen && hoursData"
            class="max-h-[268px] w-content p-2 bg-white rounded-lg shadow-sm border border-slate-200 justify-start items-start flex flex-col absolute z-10 mt-2"
        >
            <div
                v-for="(day, i) in openHours"
                :key="i"
                class="w-full h-9 p-2 rounded items-center justify-between flex"
            >
                <span
                    class="grow shrink basis-0 text-slate-800 text-sm font-normal leading-tight text-left whitespace-nowrap"
                >
                    {{ day.day }}
                </span>
                <span
                    class="text-right text-slate-800 text-sm font-normal leading-tight whitespace-nowrap ml-2"
                >
                    {{ day.time }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import LoungeIcon from '@/components/tools/lounges/partials/lounge-icon/lounge-icon.vue';
import Icon from '@/components/ui/icon/icon.vue';
import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(customParseFormat);
dayjs.extend(utc);

const props = defineProps({
    isOpen: {
        type: [Boolean, String],
        default: false,
    },
    hoursData: {
        type: [Object, null],
        required: true,
    },
    buttonColor: {
        type: String,
        default: 'text-slate-800',
    },
    timing: {
        type: [String, null],
        required: false,
    },
});

const dropdownOpen = ref(false);

const setDropdownOpen = (v) => {
    dropdownOpen.value = v;
};

const openHours = computed(() => {
    const week = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];

    if (!_.isEmpty(props.hoursData)) {
        return week.map((day) => {
            let item = props.hoursData.business_hours.find((s) =>
                s.split(' ')[0].includes(day.substring(0, 2))
            );

            if (!item) {
                return { day, time: 'Hours unavailable' };
            }

            const [startTime, endTime] = item.split(' ')[1].split('-');

            const formattedStart = dayjs(startTime, 'HH:mm').format('h:mma');
            const formattedEnd = dayjs(endTime, 'HH:mm').format('h:mma');

            let formattedTimeString = `${formattedStart} - ${formattedEnd}`;

            if (startTime === '00:00' && endTime === '00:00') {
                formattedTimeString = 'Open 24 hours';
            }

            return { day, time: formattedTimeString };
        });
    }
    return [];
});

const todaysHours = computed(() => {
    if (!_.isEmpty(props.hoursData)) {
        const today = dayjs()
            .utcOffset(props.hoursData.utc_offset)
            .format('dddd');
        return openHours.value.find((day) => day.day === today);
    }
    return { time: '' };
});

const openNowText = computed(() => {
    if (props.isOpen && !todaysHours.value.time.includes('Open 24 hours')) {
        return '(Open Now)';
    } else if (!props.isOpen) {
        return '(Closed now)';
    }
    return '';
});
</script>
